<script setup>
import { ref } from 'vue';

const props = defineProps({
    onboardingUrl: {
        type: String,
        required: true,
    },
    discoverUrl: {
        type: String,
        required: true,
    },
});

const showComparison = ref(false);

const links = [
    {
        key: 'onboarding',
        icon: 'fa-solid fa-hand-wave',
        title: 'Personal invite link',
        description: 'Takes them to your personal welcome page and a guided onboarding.',
        hint: 'Best for warm intros — people you’ve already talked to.',
        url: props.onboardingUrl,
    },
    {
        key: 'discover',
        icon: 'fa-solid fa-sparkles',
        title: 'Discover link',
        description: 'A 60-second chat with Flowie that shows them the hours, money and AI team myflow unlocks — then signs them up on the spot.',
        hint: 'Best for cold shares — social media, communities, newsletters.',
        url: props.discoverUrl,
    },
];
</script>

<template>
    <div class="">
        <div class="grid gap-4 sm:grid-cols-2">
            <div
                v-for="link in links"
                :key="link.key"
                class="flex flex-col rounded-2xl border border-gray-200 bg-gray-50 p-4"
            >
                <p class="mb-1 text-sm font-semibold text-gray-900">
                    <i class="mr-1 text-myflowPurple" :class="link.icon"></i>
                    {{ link.title }}
                </p>
                <p class="text-xs text-gray-600">{{ link.description }}</p>
                <p class="mt-1 text-xs italic text-gray-500">{{ link.hint }}</p>

                <div class="mt-auto pt-3">
                    <div class="rounded-lg border border-gray-200 bg-white p-2.5">
                        <input
                            class="w-full max-w-full truncate bg-transparent text-sm focus:outline-none"
                            type="text"
                            :value="link.url"
                            readonly
                            @focus="$event.target.select()"
                        >
                    </div>
                    <link-field
                        class="mt-3 flex justify-center space-x-4"
                        :url="link.url"
                    ></link-field>
                </div>
            </div>
        </div>

        <div class="mt-4 text-center">
            <button
                type="button"
                class="text-xs text-gray-500 underline decoration-gray-300 hover:text-myflowPurple hover:decoration-myflowPurple"
                @click="showComparison = true"
            >
                Which link should I use?
            </button>
        </div>

        <p class="mt-4 text-xs text-center text-gray-500"><i>Tip: Share by email, message, or in your community. Opening a link yourself won’t work as expected since you’re already logged in.</i></p>

        <modal-basic
            :open="showComparison"
            @closed="showComparison = false"
        >
            <template #header>Two links, same reward</template>
            <template #body>
                <div class="space-y-5 text-sm text-gray-700">
                    <p>
                        Both links are yours — every signup through either one is tracked to you
                        and counts toward your commission. The only difference is what your
                        invitee sees first.
                    </p>

                    <div>
                        <p class="font-semibold text-gray-900">
                            <i class="fa-solid fa-hand-wave mr-1 text-myflowPurple"></i>
                            Personal invite link
                        </p>
                        <ul class="mt-1 list-disc pl-5 space-y-1 text-gray-600">
                            <li>Opens your personal welcome page, with your name and branding.</li>
                            <li>Guided onboarding right after registration.</li>
                            <li>Use it when the introduction is personal — you've spoken to them, they know who you are.</li>
                        </ul>
                    </div>

                    <div>
                        <p class="font-semibold text-gray-900">
                            <i class="fa-solid fa-sparkles mr-1 text-myflowPurple"></i>
                            Discover link
                        </p>
                        <ul class="mt-1 list-disc pl-5 space-y-1 text-gray-600">
                            <li>Starts a 60-second chat where Flowie calculates the hours and revenue myflow could unlock for them.</li>
                            <li>Shows them their personal AI team before they even register.</li>
                            <li>Registration is prefilled from the chat — just a password and they're in.</li>
                            <li>Use it when the link has to sell itself — social posts, communities, newsletters, bios.</li>
                        </ul>
                    </div>

                    <p class="text-xs italic text-gray-500">
                        Not sure? Warm contact → personal invite. Everyone else → discover.
                    </p>
                </div>
            </template>
        </modal-basic>
    </div>
</template>
